import type { WallCard } from '@walls/store';
import { getCardShape, isImageCard } from './cardDisplay';

export type CardResizeHandle = 'e' | 's' | 'se';

interface CardResizeSize {
  height: number;
  width: number;
}

interface GetResizedCardSizeOptions {
  aspectRatio?: number | null;
  card: WallCard;
  deltaX: number;
  deltaY: number;
  handle: CardResizeHandle;
  startSize: CardResizeSize;
}

const minRectangleWidth = 210;
const minTextHeight = 38;
const minImageHeight = 80;
const minShapeSize = 100;

export function getResizedCardSize({
  aspectRatio,
  card,
  deltaX,
  deltaY,
  handle,
  startSize,
}: GetResizedCardSizeOptions): CardResizeSize {
  if (isImageCard(card) && isValidAspectRatio(aspectRatio)) {
    return getAspectRatioResizedCardSize({
      aspectRatio,
      deltaX,
      deltaY,
      handle,
      minSize: getMinimumCardSize(card, aspectRatio),
      startSize,
    });
  }

  const minSize = getMinimumCardSize(card);
  const shouldResizeWidth = handle === 'e' || handle === 'se';
  const shouldResizeHeight = handle === 's' || handle === 'se';

  return {
    height: shouldResizeHeight
      ? Math.max(minSize.height, Math.round(startSize.height + deltaY))
      : startSize.height,
    width: shouldResizeWidth
      ? Math.max(minSize.width, Math.round(startSize.width + deltaX))
      : startSize.width,
  };
}

export function getMinimumCardSize(card: WallCard, aspectRatio?: number | null): CardResizeSize {
  const shape = getCardShape(card);

  if (shape === 'circle' || shape === 'triangle') {
    return { height: minShapeSize, width: minShapeSize };
  }

  if (isImageCard(card) && isValidAspectRatio(aspectRatio)) {
    const width = Math.max(minRectangleWidth, Math.round(minImageHeight * aspectRatio));

    return {
      height: Math.round(width / aspectRatio),
      width,
    };
  }

  return {
    height: isImageCard(card) ? minImageHeight : minTextHeight,
    width: minRectangleWidth,
  };
}

function getAspectRatioResizedCardSize({
  aspectRatio,
  deltaX,
  deltaY,
  handle,
  minSize,
  startSize,
}: {
  aspectRatio: number;
  deltaX: number;
  deltaY: number;
  handle: CardResizeHandle;
  minSize: CardResizeSize;
  startSize: CardResizeSize;
}): CardResizeSize {
  if (handle === 's') {
    return clampImageSizeToMinimum(
      {
        height: Math.round(startSize.height + deltaY),
        width: Math.round((startSize.height + deltaY) * aspectRatio),
      },
      aspectRatio,
      minSize
    );
  }

  if (handle === 'se' && Math.abs(deltaY) > Math.abs(deltaX)) {
    return clampImageSizeToMinimum(
      {
        height: Math.round(startSize.height + deltaY),
        width: Math.round((startSize.height + deltaY) * aspectRatio),
      },
      aspectRatio,
      minSize
    );
  }

  return clampImageSizeToMinimum(
    {
      height: Math.round((startSize.width + deltaX) / aspectRatio),
      width: Math.round(startSize.width + deltaX),
    },
    aspectRatio,
    minSize
  );
}

function clampImageSizeToMinimum(
  size: CardResizeSize,
  aspectRatio: number,
  minSize: CardResizeSize
): CardResizeSize {
  if (size.width >= minSize.width && size.height >= minSize.height) {
    return size;
  }

  if (size.width < minSize.width) {
    return {
      height: Math.round(minSize.width / aspectRatio),
      width: minSize.width,
    };
  }

  return {
    height: minSize.height,
    width: Math.round(minSize.height * aspectRatio),
  };
}

function isValidAspectRatio(aspectRatio: number | null | undefined): aspectRatio is number {
  return typeof aspectRatio === 'number' && Number.isFinite(aspectRatio) && aspectRatio > 0;
}
